import { useMemo } from 'react';

import { useStyleContext } from '@/src/providers';

export type VitrinaBlock =
  | 'trending'
  | 'limited'
  | 'bestItems'
  | 'imageToVideo'
  | 'storyLines'
  | 'aiVideos'
  | 'stickers'
  | 'videoVeo3'
  | 'videoStyleCategories'
  | 'effects'
  | 'photoSessions'
  | 'photoByPhoto'
  | 'effectsCategories'
  | 'styles'
  | 'videoLooks'
  | 'videoStyles'
  | 'combiVideos';

export const calculateVitrinaPositions = (
  visibility: Record<VitrinaBlock, boolean>,
): Record<VitrinaBlock, number> => {
  const order: VitrinaBlock[] = [
    'trending',
    'limited',
    'bestItems',
    'imageToVideo',
    'storyLines',
    'aiVideos',
    'stickers',
    'videoVeo3',
    'videoStyleCategories',
    'effects',
    'photoSessions',
    'photoByPhoto',
    'effectsCategories',
    'styles',
    'videoLooks',
    'videoStyles',
    'combiVideos',
  ];

  const positions: Partial<Record<VitrinaBlock, number>> = {};
  let counter = 1;

  for (const block of order) {
    if (visibility[block]) {
      positions[block] = counter++;
    }
  }

  return positions as Record<VitrinaBlock, number>;
};

export const useVitrinaPosition = (block: VitrinaBlock): number => {
  const {
    trendingItems,
    limited,
    bestItems,
    aiVideos,
    stickers,
    videoStyleCategories,
    effects,
    storyLines,
    effectsCategories,
    photoSessions,
    styleCategories,
    videoLooks,
    videoStyles,
    combiVideos,
    videoVeo3,
  } = useStyleContext();

  return useMemo(() => {
    const visibility: Record<VitrinaBlock, boolean> = {
      trending: trendingItems.length > 0,
      limited: limited.length > 0,
      bestItems: bestItems.length > 0,
      imageToVideo: true, // Always visible
      storyLines: storyLines.length > 0,
      aiVideos: aiVideos.length > 0,
      stickers: stickers.length > 0,
      videoVeo3: videoVeo3.length > 0,
      videoStyleCategories: videoStyleCategories.length > 0,
      effects: effects.length > 0,
      photoSessions: photoSessions.length > 0,
      photoByPhoto: true, // Always visible
      effectsCategories: effectsCategories.some(
        (cat) => cat.effects.length > 0,
      ),
      styles: styleCategories.some((cat) => cat.styles.length > 0),
      videoLooks: videoLooks.length > 0,
      videoStyles: videoStyles.length > 0,
      combiVideos: combiVideos.length > 0,
    };

    const positions = calculateVitrinaPositions(visibility);
    return positions[block] || -1;
  }, [
    trendingItems.length,
    limited.length,
    bestItems.length,
    storyLines.length,
    aiVideos.length,
    stickers.length,
    videoStyleCategories.length,
    effectsCategories,
    effects.length,
    photoSessions.length,
    styleCategories,
    videoVeo3,
    videoLooks.length,
    videoStyles.length,
    combiVideos.length,
    block,
  ]);
};
